import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, queryClient, useAction, useApi } from '../api';
import { ConsoleBrand, useCan } from '../auth';
import { fmtDate, Notice, Section, Tag } from '../ui/kit';
import { RecommendedProtection } from './RecommendedProtection';

interface ChecklistItem { key: string; label: string; done: boolean; detail: string; href: string; action: string }
interface Checklist { items: ChecklistItem[]; completed: number; total: number; complete: boolean; dismissed: boolean }

/** "Get started" panel on the dashboard. Every status comes from /onboarding/checklist, which
 * derives it from the database on each request — the console never marks a step done itself. */
export function GetStarted() {
  const q = useApi<Checklist>('/onboarding/checklist', { refetchInterval: 30_000 });
  const dismiss = useAction(() => api('/onboarding/checklist/dismiss', { method: 'POST' }));
  const restore = useAction(() => api('/onboarding/checklist/restore', { method: 'POST' }));
  const can = useCan();
  const [recommend, setRecommend] = useState(false);
  // One-click recommended protection needs both rights; otherwise the step links to Policies.
  const oneClick = can('policies:write') && can('settings:write');
  const c = q.data;
  if (!c || c.complete) return null;
  if (c.dismissed) {
    return (
      <div className="actions mb-16">
        <button className="btn ghost small" onClick={() => restore.mutate(undefined)}>Show the setup checklist ({c.completed} of {c.total} done)</button>
      </div>
    );
  }
  const next = c.items.find((i) => !i.done);
  return (
    <Section
      title="Get started"
      aside={<>{c.completed} of {c.total} done · <button className="btn ghost small" onClick={() => dismiss.mutate(undefined)}>Dismiss</button></>}
    >
      <div className="progress" role="progressbar" aria-label="Setup progress" aria-valuemin={0} aria-valuemax={c.total} aria-valuenow={c.completed}>
        <span style={{ width: `${(c.completed / c.total) * 100}%` }} />
      </div>
      <ol className="checklist" aria-label="Setup checklist">
        {c.items.map((i) => (
          <li key={i.key} className={i.done ? 'done' : undefined} aria-label={`${i.label}: ${i.done ? 'done' : 'not done'}`}>
            <div>
              <div className="label">{i.label}</div>
              <div className="sub">{i.detail}</div>
            </div>
            {i.done ? <Tag tone="allow">Done</Tag>
              : i.key === 'protection' && oneClick ? <button type="button" className={`btn small${i === next ? ' primary' : ''}`} onClick={() => setRecommend(true)}>{i.action}…</button>
                : <Link className={`btn small${i === next ? ' primary' : ''}`} to={i.href}>{i.action}</Link>}
          </li>
        ))}
      </ol>
      {recommend && <RecommendedProtection onClose={() => setRecommend(false)} />}
    </Section>
  );
}

/** Shows a one-time link with a copy button. */
export function OneTimeLink({ url, expiresAt, onDone, what }: { url: string; expiresAt: string; onDone: () => void; what: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Notice tone="allow" role="status">
      {what} — copy it now and send it over a trusted channel. It works once and expires {fmtDate(expiresAt)}. Ward stores only a hash, so it cannot be shown again.
      <div className="copy-row">
        <input aria-label="Invitation link" readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
        <button className="btn small" onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
          } catch {
            setCopied(false);
          }
        }}>{copied ? 'Copied' : 'Copy'}</button>
        <button className="btn small" onClick={onDone}>Done</button>
      </div>
    </Notice>
  );
}

interface InvitationInfo { email: string; name: string; role: string; orgName: string; orgSlug: string; expiresAt: string; passwordLogin: boolean; sso: boolean }

/**
 * Public page for accepting an administrator invitation. The token is read from the URL
 * fragment (never sent to the server in a URL) and removed from the address bar at once.
 */
export function AcceptInvitation() {
  const nav = useNavigate();
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [state, setState] = useState<'loading' | 'invalid' | 'ready'>(token ? 'loading' : 'invalid');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
    if (!token) return;
    api<InvitationInfo>('/auth/invitation/inspect', { method: 'POST', body: { token } })
      .then((i) => { setInfo(i); setState('ready'); })
      .catch((e) => { setState('invalid'); if (e instanceof ApiError && e.status === 429) setError('Too many attempts. Wait a minute and reload this page.'); });
  }, [token]);

  const submit = async () => {
    setError(null);
    if (pw !== pw2) return setError('The passwords do not match.');
    if (pw.length < 12) return setError('Use at least 12 characters.');
    setBusy(true);
    try {
      await api('/auth/invitation/accept', { method: 'POST', body: { token, password: pw } });
      queryClient.clear();
      nav('/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? (e.status === 404 ? 'This invitation is no longer valid. Ask your administrator for a new one.' : e.status === 429 ? 'Too many attempts. Wait a minute and try again.' : e.message) : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  if (state !== 'ready' || !info) {
    return (
      <div className="invite">
        <div className="panel">
          <ConsoleBrand />
          <h1>{state === 'loading' ? 'Checking your invitation…' : 'Invitation not valid'}</h1>
          {state === 'invalid' && <p className="muted">{error ?? 'This invitation link is invalid, has expired or has already been used. Ask the person who invited you to send a new one.'}</p>}
          {state === 'invalid' && <Link to="/login">Go to sign in</Link>}
        </div>
      </div>
    );
  }
  const role = info.role.replace('_', ' ');
  return (
    <div className="invite">
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <ConsoleBrand />
        <h1>Join {info.orgName}</h1>
        <p className="muted m-0">You were invited as <b>{role}</b> ({info.email}). This link expires {fmtDate(info.expiresAt)} and works once.</p>
        {error && <Notice tone="block" role="alert">{error}</Notice>}
        {info.passwordLogin ? (
          <>
            <input type="email" autoComplete="username" value={info.email} readOnly hidden />
            <label className="field">Choose a password <span className="hint">at least 12 characters</span><input type="password" autoComplete="new-password" minLength={12} maxLength={200} value={pw} onChange={(e) => setPw(e.target.value)} required /></label>
            <label className="field">Repeat password<input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required /></label>
            <button className="btn primary" disabled={busy}>{busy ? 'Setting up…' : 'Set password and sign in'}</button>
          </>
        ) : !info.sso ? <Notice tone="warn">Password sign-in is disabled on this server and single sign-on is not enabled for {info.orgName} yet. Ask an owner to enable Microsoft Entra ID sign-in.</Notice> : null}
        {info.sso && (
          <>
            <div className="section-head mt-4"><span className="muted">{info.passwordLogin ? 'Or use your Microsoft account' : 'Sign in with your Microsoft account'}</span></div>
            <a className="btn" href={`/api/v1/auth/entra/start?org=${encodeURIComponent(info.orgSlug)}&login_hint=${encodeURIComponent(info.email)}`}>Sign in with Microsoft</a>
          </>
        )}
      </form>
    </div>
  );
}
